import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Warehouse, Plus, Trash2, Loader2, Building2 } from 'lucide-react';
import organizationsService, { Organization, WarehouseContact } from '@/services/organizations.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useTranslation } from 'react-i18next';

/**
 * Settings → Lager: Depo çalışanlarının NATIVE Interne Konversation (email'siz) açabileceği
 * hedef ORGANİZASYONLAR. Depo UI'ındaki "Support-Anfrage" butonu bu org'lara org-içi ticket açar.
 */
export default function WarehouseSettingsSection() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => organizationsService.getOrganizations(),
  });
  const orgs: Organization[] = data?.data || [];
  const warehouses = orgs.filter((o) => o.is_warehouse);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-base font-semibold text-slate-900">{t('agent:dept.warehouse', 'Lager')}</h3>
        <p className="text-sm text-slate-500">
          Lege fest, an welche Organisationen Lager-Mitarbeiter interne Anfragen stellen können.
          Anfragen laufen als <span className="font-semibold">{t('ticket:ui.internalConv', 'Interne Konversation')}</span> (ohne E-Mail) — die Ziel-Organisation sieht sie sofort in ihrer Ticketliste.
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : warehouses.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 py-10 text-center">
          <Warehouse className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-2 text-sm font-medium text-slate-500">{t('common:x4.noWarehouse', 'Kein Lager vorhanden')}</p>
          <p className="text-xs text-slate-400">Lege unter „Organisationen" eine Organisation mit „Lager?" an.</p>
        </div>
      ) : (
        warehouses.map((org) => (
          <WarehouseTargetsCard
            key={org.id}
            org={org}
            allOrgs={orgs}
            onSaved={() => qc.invalidateQueries({ queryKey: ['organizations'] })}
          />
        ))
      )}
    </div>
  );
}

function WarehouseTargetsCard({ org, allOrgs, onSaved }: { org: Organization; allOrgs: Organization[]; onSaved: () => void }) {
  const { t } = useTranslation();
  const [targets, setTargets] = useState<WarehouseContact[]>(
    (Array.isArray(org.warehouse_contacts) ? org.warehouse_contacts : []).filter((c: any) => c && c.org_id)
  );
  const [newOrgId, setNewOrgId] = useState('');
  const [newLabel, setNewLabel] = useState('');

  const orgName = (id?: string) => allOrgs.find((o) => o.id === id)?.name || id || '?';
  const candidates = allOrgs.filter((o) => o.id !== org.id && !targets.some((t: any) => t.org_id === o.id));

  const saveM = useMutation({
    mutationFn: (next: WarehouseContact[]) =>
      organizationsService.updateOrganization(org.id, {
        name: org.name,
        domain: org.domain,
        support_email: org.support_email,
        warehouse_contacts: next,
      } as any),
    onSuccess: () => { toast.success(t('common:x1.saved', 'Gespeichert')); onSaved(); },
    onError: () => toast.error(t('common:state.saveFailed', 'Speichern fehlgeschlagen')),
  });

  const add = () => {
    if (!newOrgId) { toast.error(t('ticket:ph.selectOrg', 'Organisation wählen')); return; }
    const next = [...targets, { org_id: newOrgId, label: newLabel.trim() || undefined } as any];
    setTargets(next);
    setNewOrgId(''); setNewLabel('');
    saveM.mutate(next);
  };
  const remove = (orgId: string) => {
    const next = targets.filter((t: any) => t.org_id !== orgId);
    setTargets(next);
    saveM.mutate(next);
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-center gap-2">
        <Warehouse className="h-4 w-4 text-orange-500" />
        <span className="text-sm font-semibold text-slate-800">{org.name}</span>
        {org.support_email && <span className="text-xs text-slate-400">· {org.support_email}</span>}
        {saveM.isPending && <Loader2 className="ml-auto h-3.5 w-3.5 animate-spin text-slate-400" />}
      </div>

      {targets.length === 0 ? (
        <p className="mb-3 rounded-md border border-dashed border-slate-200 py-3 text-center text-xs text-slate-400">
          Noch keine Ziel-Organisationen — Lager-Mitarbeiter können keine Anfragen stellen.
        </p>
      ) : (
        <div className="mb-3 space-y-1.5">
          {targets.map((t: any) => (
            <div key={t.org_id} className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-2 ring-1 ring-slate-900/[0.04]">
              <Building2 className="h-3.5 w-3.5 shrink-0 text-slate-400" />
              <span className="text-sm font-medium text-slate-700">{t.label || orgName(t.org_id)}</span>
              {t.label && <span className="text-xs text-slate-400">· {orgName(t.org_id)}</span>}
              <button onClick={() => remove(t.org_id)} className="ml-auto rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600" title={t('ticket:action.remove', 'Entfernen')}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[200px] flex-[2]">
          <Label className="text-xs text-slate-500">{t('common:x4.targetOrganization', 'Ziel-Organisation')}</Label>
          <Select value={newOrgId} onValueChange={setNewOrgId}>
            <SelectTrigger className="mt-1 h-9"><SelectValue placeholder={t('common:x4.chooseOrgDots', 'Organisation wählen…')} /></SelectTrigger>
            <SelectContent>
              {candidates.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[140px] flex-1">
          <Label className="text-xs text-slate-500">{t('common:x1.labelOptional', 'Bezeichnung (optional)')}</Label>
          <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder={t('common:x4.egPurchasingBoss', 'z. B. Einkauf / Chef')} className="mt-1 h-9" />
        </div>
        <Button onClick={add} disabled={saveM.isPending || !newOrgId} className="h-9 bg-[#0F99FD] hover:bg-[#0c84e0]">
          <Plus className="mr-1 h-4 w-4" /> {'Hinzufügen'}
        </Button>
      </div>
    </div>
  );
}
